import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ProcessingState } from '../types';
import type { ProcessingStatus, DynamicCase } from '../../types';
import { apiSlice } from '../api/apiSlice';

const initialState: ProcessingState = {
  activeProcessing: {},
  backgroundProcessingStatus: null,
  pendingNotification: null,
  recentlyProcessed: [],
  processingHistory: [],
};

const processingSlice = createSlice({
  name: 'processing',
  initialState,
  reducers: {
    // Start new processing operation
    startProcessing: (state, action: PayloadAction<{
      processingId: string;
      fieldId: string;
      rawContent: string;
      promptInstructions?: string;
    }>) => {
      const { processingId, fieldId, rawContent, promptInstructions } = action.payload;
      
      console.log('🤖 Starting processing in state:', processingId);
      
      // Add to active processing with initial status
      state.activeProcessing[processingId] = {
        status: 'pending',
        progress: 0,
        currentStep: 'Initializing processing...',
      };
      
      // Add to processing history
      state.processingHistory.unshift({
        processingId,
        fieldId,
        startTime: new Date().toISOString(),
        status: 'pending',
        success: false,
      });
      
      // Keep only last 100 entries in history
      state.processingHistory = state.processingHistory.slice(0, 100);
    },
    
    // Update processing status
    updateProcessingStatus: (state, action: PayloadAction<{
      processingId: string;
      status: ProcessingStatus;
    }>) => {
      const { processingId, status } = action.payload;
      
      console.log(`🔄 Updating processing ${processingId}: ${status.status} (${status.progress}%)`);
      
      // Update active processing status
      state.activeProcessing[processingId] = status;
      
      // Update history entry
      const historyIndex = state.processingHistory.findIndex(h => h.processingId === processingId);
      if (historyIndex !== -1) {
        state.processingHistory[historyIndex].status = status.status;
        
        if (status.status === 'completed' || status.status === 'failed' || status.status === 'review_required') {
          state.processingHistory[historyIndex].endTime = new Date().toISOString();
          state.processingHistory[historyIndex].success = status.status === 'completed';
          
          if (status.errors && status.errors.length > 0) {
            state.processingHistory[historyIndex].error = status.errors.join(', ');
          }
        }
      }
    },
    
    // Complete processing successfully
    completeProcessing: (state, action: PayloadAction<{
      processingId: string;
      case: DynamicCase;
    }>) => {
      const { processingId, case: processedCase } = action.payload;
      
      console.log('✅ Processing completed:', processingId);
      
      // Remove from active processing
      delete state.activeProcessing[processingId];
      
      // Add to recently processed (avoid duplicates)
      const existingIndex = state.recentlyProcessed.findIndex(c => c.id === processedCase.id);
      if (existingIndex !== -1) {
        state.recentlyProcessed[existingIndex] = processedCase;
      } else {
        state.recentlyProcessed.unshift(processedCase);
      }
      
      // Keep only last 20 recently processed cases
      state.recentlyProcessed = state.recentlyProcessed.slice(0, 20);
      
      // Update history
      const historyIndex = state.processingHistory.findIndex(h => h.processingId === processingId);
      if (historyIndex !== -1) {
        state.processingHistory[historyIndex].endTime = new Date().toISOString();
        state.processingHistory[historyIndex].status = 'completed';
        state.processingHistory[historyIndex].success = true;
      }
    },
    
    // Fail processing
    failProcessing: (state, action: PayloadAction<{
      processingId: string;
      error: string;
    }>) => {
      const { processingId, error } = action.payload;
      
      console.log('❌ Processing failed:', processingId, error);
      
      // Remove from active processing
      delete state.activeProcessing[processingId];
      
      // Update history
      const historyIndex = state.processingHistory.findIndex(h => h.processingId === processingId);
      if (historyIndex !== -1) {
        state.processingHistory[historyIndex].endTime = new Date().toISOString();
        state.processingHistory[historyIndex].status = 'failed';
        state.processingHistory[historyIndex].success = false;
        state.processingHistory[historyIndex].error = error;
      }
    },
    
    // Cancel processing
    cancelProcessing: (state, action: PayloadAction<string>) => {
      const processingId = action.payload;
      
      console.log('🛑 Cancelling processing:', processingId);
      
      // Remove from active processing
      delete state.activeProcessing[processingId];
      
      // Update history
      const historyIndex = state.processingHistory.findIndex(h => h.processingId === processingId);
      if (historyIndex !== -1) {
        state.processingHistory[historyIndex].endTime = new Date().toISOString();
        state.processingHistory[historyIndex].status = 'failed';
        state.processingHistory[historyIndex].success = false;
        state.processingHistory[historyIndex].error = 'Cancelled by user';
      }
    },
    
    // Set or clear the background processing status for the global indicator
    setBackgroundProcessingStatus: (state, action: PayloadAction<ProcessingStatus | null>) => {
      state.backgroundProcessingStatus = action.payload;
    },

    // Set or clear a global notification to display across any page
    setPendingNotification: (state, action: PayloadAction<{ message: string; severity: 'success' | 'error' | 'warning' | 'info' } | null>) => {
      state.pendingNotification = action.payload;
    },

    // Clear completed/failed processing from active list
    cleanupProcessing: (state) => {
      const toDelete: string[] = [];
      
      Object.entries(state.activeProcessing).forEach(([processingId, status]) => {
        if (status.status === 'completed' || status.status === 'failed' || status.status === 'review_required') {
          toDelete.push(processingId);
        }
      });
      
      toDelete.forEach(processingId => {
        delete state.activeProcessing[processingId];
      });
      
      console.log(`🧹 Cleaned up ${toDelete.length} completed/failed processes`);
    },
    
    // Clear recently processed cases
    clearRecentlyProcessed: (state) => {
      console.log('🧹 Clearing recently processed cases');
      state.recentlyProcessed = [];
    },
    
    // Clear processing history
    clearProcessingHistory: (state) => {
      console.log('🧹 Clearing processing history');
      state.processingHistory = [];
    },
    
    // Clear all processing data
    clearAllProcessing: (state) => {
      console.log('🧹 Clearing all processing data');
      return { ...initialState };
    },
    
    // Remove old history entries (cleanup)
    pruneProcessingHistory: (state, action: PayloadAction<{ olderThanDays: number }>) => {
      const { olderThanDays } = action.payload;
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);
      
      const originalCount = state.processingHistory.length;
      state.processingHistory = state.processingHistory.filter(entry => {
        const entryDate = new Date(entry.startTime);
        return entryDate > cutoffDate;
      });
      
      const removedCount = originalCount - state.processingHistory.length;
      console.log(`🧹 Pruned ${removedCount} old processing history entries`);
    },
  },
  
  extraReducers: (builder) => {
    // Handle RTK Query processCase
    builder
      .addMatcher(apiSlice.endpoints.processCase.matchPending, (state, action) => {
        const { fieldId, rawContent, promptInstructions } = action.meta.arg.originalArgs;
        
        // Use all destructured variables to avoid TypeScript errors
        console.log('📡 RTK Query: Processing case started for field:', fieldId, 
                   `(${rawContent.length} chars)`, promptInstructions ? 'with instructions' : 'no instructions');
      })
      .addMatcher(apiSlice.endpoints.processCase.matchFulfilled, (state, action) => {
        const response = action.payload; // CaseProcessingResponse
        const { fieldId } = action.meta.arg.originalArgs; // Only destructure what we use
        
        console.log('📡 RTK Query: Processing started with ID:', response.processingId);
        
        // Add to active processing
        state.activeProcessing[response.processingId] = {
          status: 'pending',
          progress: 0,
          currentStep: response.message || 'Processing started...',
        };
        
        // Add to history
        state.processingHistory.unshift({
          processingId: response.processingId,
          fieldId,
          startTime: new Date().toISOString(),
          status: 'pending',
          success: false,
        });
        
        // Keep only last 100 entries
        state.processingHistory = state.processingHistory.slice(0, 100);
      })
      .addMatcher(apiSlice.endpoints.processCase.matchRejected, (state, action) => {
        const { fieldId } = action.meta.arg.originalArgs; // Only destructure what we use
        console.error('📡 RTK Query: Processing failed to start for field:', fieldId, action.error);
        // Error is handled by the calling component
      })
      
      // Handle processing status queries
      .addMatcher(apiSlice.endpoints.getProcessingStatus.matchFulfilled, (state, action) => {
        const status = action.payload; // ProcessingStatus
        const processingId = action.meta.arg.originalArgs; // string
        
        // Update active processing status
        if (state.activeProcessing[processingId]) {
          state.activeProcessing[processingId] = status;
        }
        
        // Update history
        const historyIndex = state.processingHistory.findIndex(h => h.processingId === processingId);
        if (historyIndex !== -1) {
          state.processingHistory[historyIndex].status = status.status;
          
          if (status.status === 'completed' || status.status === 'failed' || status.status === 'review_required') {
            state.processingHistory[historyIndex].endTime = new Date().toISOString();
            state.processingHistory[historyIndex].success = status.status === 'completed';
            
            if (status.errors && status.errors.length > 0) {
              state.processingHistory[historyIndex].error = status.errors.join(', ');
            }
          }
        }
      });
  },
});

export const {
  startProcessing,
  updateProcessingStatus,
  setBackgroundProcessingStatus,
  setPendingNotification,
  completeProcessing,
  failProcessing,
  cancelProcessing,
  cleanupProcessing,
  clearRecentlyProcessed,
  clearProcessingHistory,
  clearAllProcessing,
  pruneProcessingHistory,
} = processingSlice.actions;

export default processingSlice.reducer;